<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">B2B 價格</h1>
      </div>
    </div>

    <div class="page-container">
    <p class="page-desc text-muted">請選擇商品，依會員等級設定各規格（SKU）的 B2B 單價。</p>

    <aside
      class="b2b-pricing-tip"
      :class="b2bTipToneClass"
      role="region"
      aria-labelledby="b2b-pricing-tip-title"
    >
      <i class="b2b-pricing-tip__icon ri-lightbulb-flash-line" aria-hidden="true" />
      <div class="b2b-pricing-tip__body">
        <h2 id="b2b-pricing-tip-title" class="b2b-pricing-tip__title">商城須為 B2B 模式</h2>
        <p class="b2b-pricing-tip__lead">
          此頁維護的「會員等級 B2B 單價」僅在<strong>電商設定</strong>內將<strong>商城模式</strong>設為
          <strong>B2B（企業客戶）</strong>後，才會於 B2B 結帳／報價流程套用；若維持 B2C，此處價格不會生效。
        </p>
        <p class="b2b-pricing-tip__path">
          設定路徑：<span class="b2b-pricing-tip__kbd">電商設定</span>
          → <span class="b2b-pricing-tip__kbd">結帳與收款</span> → <span class="b2b-pricing-tip__kbd">商城模式</span>
        </p>
        <p v-if="commerceMode === 'b2b'" class="b2b-pricing-tip__status b2b-pricing-tip__status--ok">
          <i class="ri-checkbox-circle-line" aria-hidden="true" />
          目前商城已為 B2B 模式，可安心設定各商品等級價格。
        </p>
        <p v-else-if="commerceMode === 'b2c'" class="b2b-pricing-tip__status b2b-pricing-tip__status--warn">
          <i class="ri-error-warning-line" aria-hidden="true" />
          目前為 B2C 模式，請先改為 B2B，此處設定的價格才會套用。
        </p>
        <router-link
          :to="{ name: 'ClientCommerceSettings' }"
          class="btn btn-sm b2b-pricing-tip__link"
        >
          <i class="ri-settings-3-line" aria-hidden="true" />
          前往電商設定
        </router-link>
      </div>
    </aside>

    <div class="page-toolbar">
      <div class="search-box">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="搜尋商品名稱、SKU、條碼…"
          @input="handleSearch"
        />
        <i class="ri-search-line" />
      </div>
      <div class="filter-group">
        <select v-model="filterStatus" class="form-control" @change="handleFilterChange">
          <option value="">全部狀態</option>
          <option value="active">上架</option>
          <option value="inactive">下架</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loading-inline">載入中…</div>
    <div v-else-if="error" class="alert alert-danger">{{ error }}</div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>圖片</th>
            <th>商品名稱</th>
            <th>SKU</th>
            <th>售價</th>
            <th>狀態</th>
            <th style="width: 100px">B2B 價格</th>
            <th style="width: 140px">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!products.length">
            <td colspan="7" class="empty-state">沒有符合的商品</td>
          </tr>
          <tr
            v-for="p in products"
            :key="p.id"
            class="row-click"
            @click="openProduct(p)"
          >
            <td @click.stop>
              <img
                v-if="productThumbUrl(p)"
                :src="productThumbUrl(p)"
                :alt="p.name ? `${p.name} 縮圖` : '商品縮圖'"
                class="product-thumb"
                loading="lazy"
              />
              <span v-else class="text-muted">—</span>
            </td>
            <td>
              {{ p.name || '—' }}
              <span v-if="p.productType === 'bundle'" class="badge badge-bundle">套組</span>
            </td>
            <td>{{ p.sku || '—' }}</td>
            <td>{{ formatCurrency(p.unitPrice) }}</td>
            <td>
              <span :class="p.status === 'active' ? 'badge badge-success' : 'badge badge-secondary'">
                {{ p.status === 'active' ? '上架' : '下架' }}
              </span>
            </td>
            <td>
              <span v-if="isB2bPriceConfigured(p)" class="badge badge-b2b-set">已設定</span>
              <span v-else class="text-muted">—</span>
            </td>
            <td @click.stop>
              <button type="button" class="btn btn-primary btn-sm" @click="openProduct(p)">
                設定 B2B 價格
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ListPagination
      v-if="total > 0"
      :page="currentPage"
      :total-pages="totalPages"
      :total="total"
      @change="goToPage"
    />
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { productService } from '@/api/modules/products.js'
import { commerceSettingsApi } from '@/api/modules/commerceSettings.js'
import { usePageTitle } from '@/composables/usePageTitle.js'

usePageTitle('B2B 價格')

const router = useRouter()

/** null：尚未載入；'b2b' | 'b2c'：來自電商設定 */
const commerceMode = ref(null)

const b2bTipToneClass = computed(() => {
  if (commerceMode.value === 'b2b') return 'b2b-pricing-tip--ok'
  if (commerceMode.value === 'b2c') return 'b2b-pricing-tip--warn'
  return 'b2b-pricing-tip--neutral'
})

const products = ref([])
const loading = ref(false)
const error = ref('')
const searchQuery = ref('')
const filterStatus = ref('')
const currentPage = ref(1)
const pageSize = ref(20)
const total = ref(0)
const searchTimeout = ref(null)

const totalPages = computed(() => Math.max(1, Math.ceil(total.value / pageSize.value)))

function formatCurrency(amount) {
  const num = Number(amount)
  if (amount === null || amount === undefined || Number.isNaN(num)) return 'NT$ 0'
  return `NT$ ${Math.round(num).toLocaleString('zh-TW', { maximumFractionDigits: 0 })}`
}

/** API includeB2bPriceSet 時之欄位；相容字串 "true" */
function isB2bPriceConfigured(p) {
  const v = p?.b2bPriceConfigured
  return v === true || v === 'true' || v === 't' || v === 1
}

/** 列表縮圖 URL（主檔或相簿首圖，由 API 合併為 imageUrl） */
function productThumbUrl(p) {
  const u = p?.imageUrl
  if (u == null) return ''
  const s = String(u).trim()
  return s || ''
}

async function fetchProducts() {
  loading.value = true
  error.value = ''
  try {
    const params = {
      page: currentPage.value,
      pageSize: pageSize.value,
      includeB2bPriceSet: true,
    }
    if (searchQuery.value) params.search = searchQuery.value
    if (filterStatus.value) params.status = filterStatus.value
    const res = await productService.getProducts(params)
    if (res && res.success && res.data) {
      products.value = res.data.items || []
      total.value = res.data.pagination?.totalItems || 0
    } else {
      error.value = (res && res.message) || '取得商品列表失敗'
    }
  } catch (e) {
    error.value = e?.response?.data?.message || e.message || '取得商品列表失敗'
  } finally {
    loading.value = false
  }
}

function handleSearch() {
  if (searchTimeout.value) clearTimeout(searchTimeout.value)
  searchTimeout.value = setTimeout(() => {
    currentPage.value = 1
    fetchProducts()
  }, 400)
}

function handleFilterChange() {
  currentPage.value = 1
  fetchProducts()
}

function goToPage(page) {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
    fetchProducts()
  }
}

function openProduct(p) {
  router.push({
    name: 'ClientCommerceB2bPricingProduct',
    params: { productId: p.id },
  })
}

async function fetchCommerceMode() {
  try {
    const res = await commerceSettingsApi.getSettings()
    const s = res?.data ?? {}
    commerceMode.value = s.commerceMode === 'b2b' ? 'b2b' : 'b2c'
  } catch {
    commerceMode.value = null
  }
}

onMounted(() => {
  fetchCommerceMode()
  fetchProducts()
})
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.page-desc {
  margin: 0 0 12px;
  font-size: 14px;
}

.b2b-pricing-tip {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding: 16px 18px;
  margin: 0 0 20px;
  border-radius: 10px;
  border: 1px solid #c7d2fe;
  background: linear-gradient(135deg, #eef2ff 0%, #f8fafc 100%);
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
}

.b2b-pricing-tip--neutral {
  border-color: #c7d2fe;
  background: linear-gradient(135deg, #eef2ff 0%, #f8fafc 100%);
}

.b2b-pricing-tip--ok {
  border-color: #86efac;
  background: linear-gradient(135deg, #ecfdf5 0%, #f8fafc 100%);
}

.b2b-pricing-tip--warn {
  border-color: #fcd34d;
  background: linear-gradient(135deg, #fffbeb 0%, #fefce8 100%);
}

.b2b-pricing-tip__icon {
  flex-shrink: 0;
  font-size: 28px;
  line-height: 1;
  color: #6366f1;
  margin-top: 2px;
}

.b2b-pricing-tip--ok .b2b-pricing-tip__icon {
  color: #16a34a;
}

.b2b-pricing-tip--warn .b2b-pricing-tip__icon {
  color: #d97706;
}

.b2b-pricing-tip__body {
  min-width: 0;
}

.b2b-pricing-tip__title {
  margin: 0 0 8px;
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.35;
}

.b2b-pricing-tip__lead {
  margin: 0 0 10px;
  font-size: 14px;
  line-height: 1.55;
  color: #334155;
}

.b2b-pricing-tip__path {
  margin: 0 0 12px;
  font-size: 13px;
  line-height: 1.5;
  color: #475569;
}

.b2b-pricing-tip__kbd {
  display: inline;
  padding: 2px 8px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(148, 163, 184, 0.45);
  font-weight: 600;
  font-size: 12px;
  color: #0f172a;
}

.b2b-pricing-tip__status {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 12px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.45;
}

.b2b-pricing-tip__status--ok {
  color: #15803d;
}

.b2b-pricing-tip__status--warn {
  color: #b45309;
}

.b2b-pricing-tip__status i {
  font-size: 18px;
  flex-shrink: 0;
}

.b2b-pricing-tip__link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
}

.search-box {
  flex: 1 1 240px;
}

.filter-group select {
  min-width: 120px;
}

.loading-inline {
  padding: 24px;
  text-align: center;
  color: #6b7280;
}

.table-wrapper table {
  min-width: 720px;
}

.product-thumb {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.row-click {
  cursor: pointer;
}

.row-click:hover {
  background: #f9fafb;
}

.empty-state {
  text-align: center;
  padding: 24px;
  color: #6b7280;
}

.pagination {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
  flex-wrap: wrap;
}

.page-info {
  font-size: 13px;
  color: #6b7280;
}

.badge-bundle {
  margin-left: 6px;
  font-size: 11px;
  vertical-align: middle;
}

.badge-b2b-set {
  background: #dbeafe;
  color: #1d4ed8;
  font-weight: 500;
}
</style>
